import { useCallback, useEffect, useState } from "react"

import { Button, Input, SelectDropdown } from "@coval/ui"
import { ChevronDown, Trash2 } from "lucide-react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import type { JsonFieldValue } from "@/types/json"

import type { ApiCallConfig } from "../types"

interface ApiWrapperTopLevelConfigFormProps {
  label: string
  config: ApiCallConfig | null | undefined
  onChange: (config: ApiCallConfig | null) => void
  onRemove: () => void
  hideHeaderValues?: boolean
}

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"]

export const ApiWrapperTopLevelConfigForm: React.FC<
  ApiWrapperTopLevelConfigFormProps
> = ({ label, config, onChange, onRemove, hideHeaderValues = false }) => {
  const [isExpanded, setIsExpanded] = useState(!!config)
  const [areHeadersRevealed, setAreHeadersRevealed] = useState(false)

  useEffect(() => {
    if (hideHeaderValues) {
      setAreHeadersRevealed(false)
    }
  }, [hideHeaderValues])
  const [localConfig, setLocalConfig] = useState<ApiCallConfig>(
    config || {
      api_url: "",
      method: "GET",
      request_body: null,
      headers: null,
      timeout: 30,
    },
  )

  // Sync localConfig with config prop when it changes (e.g., when loading from DB)
  useEffect(() => {
    if (config) {
      setLocalConfig(config)
      // Auto-expand if config has data
      if (config.api_url || config.request_body || config.headers) {
        setIsExpanded(true)
      }
    } else {
      // Reset to defaults if config is cleared
      setLocalConfig({
        api_url: "",
        method: "GET",
        request_body: null,
        headers: null,
        timeout: 30,
      })
    }
  }, [config])

  const updateConfig = useCallback(
    (updates: Partial<ApiCallConfig>) => {
      const newConfig = { ...localConfig, ...updates }
      setLocalConfig(newConfig)
      onChange(newConfig)
    },
    [localConfig, onChange],
  )

  const handleRequestBodyChange = (value: string) => {
    try {
      const parsed = value.trim() ? (JSON.parse(value) as JsonFieldValue) : null
      updateConfig({ request_body: parsed })
    } catch {
      // Invalid JSON, but we'll still update it so user can continue typing
      updateConfig({ request_body: value })
    }
  }

  const handleHeadersChange = (value: string) => {
    try {
      const parsed = value.trim() ? (JSON.parse(value) as JsonFieldValue) : null
      updateConfig({ headers: parsed })
    } catch {
      // Invalid JSON, but we'll still update it so user can continue typing
      updateConfig({ headers: value })
    }
  }

  const requestBodyValue =
    typeof localConfig.request_body === "string"
      ? localConfig.request_body
      : localConfig.request_body
        ? JSON.stringify(localConfig.request_body, null, 2)
        : ""

  const headersValue =
    typeof localConfig.headers === "string"
      ? localConfig.headers
      : localConfig.headers
        ? JSON.stringify(localConfig.headers, null, 2)
        : ""

  return (
    <div className="overflow-hidden rounded-md border border-coval-border bg-surface-0">
      <div className="flex items-center gap-1 px-3 py-2">
        <button
          type="button"
          onClick={() => {
            setAreHeadersRevealed(false)
            setIsExpanded(!isExpanded)
          }}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded px-1 py-1 text-left hover:bg-surface-1 transition-colors"
          aria-expanded={isExpanded}
        >
          <span className="truncate text-sm font-medium text-coval-text">
            {label}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-coval-text-muted transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        <Button
          icon
          variant="ghost"
          onClick={onRemove}
          tooltip={`Remove ${label}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-4 border-t border-coval-border px-4 py-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-coval-text">API URL</span>
            <Input
              placeholder="https://api.example.com/endpoint"
              value={localConfig.api_url}
              onChange={(e) => updateConfig({ api_url: e.target.value })}
              required
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-coval-text">Method</span>
              <SelectDropdown
                aria-label="Method"
                value={localConfig.method || "GET"}
                onValueChange={(method) =>
                  updateConfig({ method: method || "GET" })
                }
                options={HTTP_METHODS.map((method) => ({
                  value: method,
                  label: method,
                }))}
              />
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-coval-text">Timeout (seconds)</span>
              <Input
                type="number"
                value={String(localConfig.timeout || 30)}
                onChange={(e) =>
                  updateConfig({ timeout: parseInt(e.target.value, 10) || 30 })
                }
                min={1}
              />
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-coval-text">Request Body (JSON)</span>
            <JsonCodeEditor
              ariaLabel="Request Body (JSON)"
              placeholder={'{\n  "key": "value"\n}'}
              value={requestBodyValue}
              onChange={handleRequestBodyChange}
              rows={3}
            />
          </label>

          <div
            className="flex flex-col gap-1"
            data-private
            data-dd-privacy="mask"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setAreHeadersRevealed(false)
              }
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-coval-text">Headers (JSON)</span>
              <Button
                type="button"
                variant="ghost"
                disabled={hideHeaderValues}
                aria-expanded={areHeadersRevealed && !hideHeaderValues}
                onClick={() => setAreHeadersRevealed(!areHeadersRevealed)}
              >
                {areHeadersRevealed && !hideHeaderValues
                  ? "Hide headers"
                  : headersValue
                    ? "Show headers"
                    : "Add headers"}
              </Button>
            </div>
            <p className="text-xs text-coval-text-muted">
              Optional. Enter headers such as {`{"x-api-key": "your-api-key"}`}.
              Values stay hidden until you choose to view or edit them.
            </p>
            {areHeadersRevealed && !hideHeaderValues ? (
              <JsonCodeEditor
                ariaLabel="Headers (JSON)"
                placeholder={'{\n  "x-api-key": "your-api-key"\n}'}
                value={headersValue}
                onChange={handleHeadersChange}
                rows={3}
              />
            ) : (
              <p className="text-sm text-coval-text-muted">
                {headersValue
                  ? "Header values hidden"
                  : "No headers configured"}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
